import type { QuestionGroupTemplateContract } from '@/contracts/question-groups'
import {
  createMockQuestionGroupTemplate,
  listMockQuestionGroupTemplates,
  updateMockQuestionGroupTemplate,
} from '@/lib/mock-api/question-groups'
import { apiOrMock, requestApiEnvelope, toSourceMeta } from './http'

type CreateQuestionGroupTemplateInput = {
  title: string
  description?: string
  category?: string
  tags?: string[]
}

export async function listQuestionGroupTemplates(): Promise<QuestionGroupTemplateContract[]> {
  return apiOrMock({
    label: 'listQuestionGroupTemplates',
    api: async () => {
      const envelope =
        await requestApiEnvelope<QuestionGroupTemplateContract[]>('/api/question-groups')
      return envelope.data.map((template) =>
        normalizeQuestionGroupTemplate(template, envelope.meta),
      )
    },
    mock: listMockQuestionGroupTemplates,
  })
}

export async function createQuestionGroupTemplate(
  input: CreateQuestionGroupTemplateInput,
): Promise<QuestionGroupTemplateContract> {
  return apiOrMock({
    label: 'createQuestionGroupTemplate',
    api: async () => {
      const envelope = await requestApiEnvelope<QuestionGroupTemplateContract>(
        '/api/question-groups',
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      )
      return normalizeQuestionGroupTemplate(envelope.data, envelope.meta)
    },
    mock: () => createMockQuestionGroupTemplate(input),
  })
}

export async function saveQuestionGroupTemplate(
  template: QuestionGroupTemplateContract,
): Promise<QuestionGroupTemplateContract> {
  return apiOrMock({
    label: 'saveQuestionGroupTemplate',
    api: async () => {
      const envelope = await requestApiEnvelope<QuestionGroupTemplateContract>(
        `/api/question-groups/${template.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: template.title,
            description: template.description,
            category: template.category,
            tags: template.tags,
            questions: template.questions,
            logicJumps: template.logicJumps,
          }),
        },
      )
      return normalizeQuestionGroupTemplate(envelope.data, envelope.meta)
    },
    mock: () => updateMockQuestionGroupTemplate(template),
  })
}

function normalizeQuestionGroupTemplate(
  template: QuestionGroupTemplateContract,
  meta?: Parameters<typeof toSourceMeta>[0],
): QuestionGroupTemplateContract {
  return {
    ...template,
    sourceMeta: toSourceMeta(meta ?? template.sourceMeta),
  }
}
